import { Grid, Typography } from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import RemoveIcon from '@material-ui/icons/Remove';
import { CreateFileInput } from "common/backend/generated/create-file-input";
import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import { PropertiesCompositionLevel } from "../../backend/types/constants";
import { CostCalculationPercentageType, PropertyType } from "../../backend/types/utils";
import { CreatePropertyManageable } from "../../store/property/types";
import { CreateSaleManageable, SaleManageable } from "../../store/sale/types";
import { Mode } from "../../utils/utility";
import { ActionButton } from "../ActionButton";
import { SanitizeHTML } from "../index";
import { AcceptedFileType } from "../input/elements/FileUpload";
import { SaleField, SaleInputTypeKey } from "../input/fields";
import { PropertyField, PropertyInputTypeKey } from "../input/fields/PropertyField";
import { SubformProps } from "../SubformProps";
import { CostCalculationTable } from "./CostCalculationTable";

interface SaleRichTextPropertyProps extends SubformProps<CreatePropertyManageable | CreatePropertyManageable> {
  compositionLevel: number;
  mode: Mode;
}

const SaleRichTextProperty = (props: SaleRichTextPropertyProps) => {
  const {
    compositionLevel,
    mode,
    values,
  } = props;

  const [expanded, setExpanded] = useState(false);

  const propertyClassName = "property";
  const propertyNameClassName = "name";
  const iconButtonClassName = "icon-button";

  return (
    <Fragment>
      <Grid item className={propertyClassName + " " + propertyNameClassName}>
        {mode === Mode.Edit &&
        <PropertyField
          {...props}
          id={"key" as PropertyInputTypeKey}
          variant="filled"
          noLabels
        />
        ||
        <Typography variant="body1" color="primary" className={propertyNameClassName}>
          {values.key}
        </Typography>
        }

        <ActionButton
          className={iconButtonClassName}
          onClick={() => setExpanded(!expanded)}
          icon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowRightIcon />}
        />
      </Grid>

      {expanded &&
      <Fragment>
        <Grid item className={propertyClassName + "--rich-text"}>
          {mode === Mode.Edit &&
          <PropertyField
            {...props}
            id={"value" as PropertyInputTypeKey}
            noLabels
          />
          ||
          <SanitizeHTML html={values.value} />
          }
        </Grid>

        {compositionLevel > 0 &&
        <Grid item className={propertyClassName + " " + propertyNameClassName}>
          <SaleRichTextPropertiesManager
            {...props}
            id="properties"
            compositionLevel={compositionLevel - 1}
          />
        </Grid>
        }
      </Fragment>
      }
    </Fragment>
  );
}
export interface SaleRichTextPropertiesManagerProps extends SubformProps<CreateSaleManageable | SaleManageable | CreatePropertyManageable> {
  compositionLevel: number;
  id: string;
  mode: Mode;
  onFilesSelected?: (value: Array<AcceptedFileType>) => void;
  onUploadEnded?: (value: Array<AcceptedFileType>) => void;
}

export const SaleRichTextPropertiesManager = (props: SaleRichTextPropertiesManagerProps) => {
  const {
    compositionLevel,
    id,
    mode,
    setFieldValue,
    setValues,
    values,
    errors,
    touched,
  } = props;

  const documents: Array<CreateFileInput | File> = 'documents' in values && values?.documents || Array<CreateFileInput | File>();

  const [costCalculationOpen, setCostCalculationOpen] = useState(true);
  const [documentsOpen, setDocumentsOpen] = useState(true);

  const { t } = useTranslation();

  const addProperty = () => {
    if (!values[id]) {
      values[id] = [];
    }

    values[id].push(new CreatePropertyManageable());

    setFieldValue(
      id,
      values[id]
    );
  }

  const removeDocument = (index: number) => {
    if ('documents' in values) {
      values.documents?.splice(
        index,
        1
      );

      setFieldValue(
        'documents',
        values.documents
      );
    }
  }

  const setPropertySubfield = (
    id:string, index: number, subkey: string, value: string
  ) => {
    values[id][index][subkey] = value;
    setFieldValue(
      id,
      values[id]
    );
  }

  const propertyClassName = "property";
  const propertyNameClassName = "name";
  const iconButtonClassName = "icon-button";

  return (
    <Grid container direction="column" className="rich-text-properties-box">
      {mode === Mode.Edit &&
      <Grid item className={propertyClassName}>
        <ActionButton
          className={iconButtonClassName + "--centered"}
          onClick={addProperty}
          icon={<AddIcon />}
        />
      </Grid>
      }

      {values[id]?.map((
        property: CreatePropertyManageable, propertyIndex: number
      ) =>
        <React.Fragment key={propertyIndex}>
          {property.type === PropertyType.RICH_TEXT &&
        <SaleRichTextProperty
          key={propertyIndex}
          mode={mode}
          setFieldValue={(
            subkey: string, value: any
          ) => setPropertySubfield(
            id,
            propertyIndex,
            subkey,
            value
          )}
          setValues={setValues}
          values={property}
          errors={errors && errors[propertyIndex] || {}}
          touched={touched && touched[propertyIndex] || {}}
          compositionLevel={compositionLevel}
        />}
        </React.Fragment>)}

      {compositionLevel === PropertiesCompositionLevel &&
      <Fragment>
        <Grid item className={propertyClassName}>
          <Typography variant="body1" color="primary" className={propertyNameClassName}>
            {t("Documents")}
          </Typography>

          <ActionButton
            className={iconButtonClassName}
            onClick={() => setDocumentsOpen(!documentsOpen)}
            icon={documentsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowRightIcon />}
          />
        </Grid>

        {documentsOpen &&
        <Grid item className={propertyClassName}>
          <Grid container direction="row" spacing={2}>
            <Grid container direction="row" spacing={1} item xs={12}>
              {documents?.map((
                document: CreateFileInput | File, index: number
              ) =>
                <Grid container direction="row" item xs={12} key={index}>
                  {mode === Mode.Edit &&
                  <Grid item xs={1}>
                    <ActionButton
                      className={iconButtonClassName + "--small"}
                      onClick={() => removeDocument(index)}
                      icon={<RemoveIcon />}
                    />
                  </Grid>
                  }

                  <Grid item xs={mode === Mode.Edit ? 11 : 12} className="grid-container">
                    <Typography className="bold text document" color="textSecondary">
                      {document.name}
                    </Typography>
                  </Grid>
                </Grid>)}
            </Grid>

            {mode === Mode.Edit &&
            <Grid item xs={12}>
              <SaleField
                {...props}
                id={"documents" as SaleInputTypeKey}
                className={iconButtonClassName + "--centered"}
                variant="icon"
                values={values as CreateSaleManageable}
              />
            </Grid>
            }
          </Grid>
        </Grid>
        }
      </Fragment>
      }

      {mode === Mode.Edit && compositionLevel === PropertiesCompositionLevel &&
      <Fragment>
        <Grid item className={propertyClassName}>
          <Typography variant="body1" color="primary" className={propertyNameClassName}>
            {t("Cost calculation")}
          </Typography>

          <ActionButton
            className={iconButtonClassName}
            onClick={() => setCostCalculationOpen(!costCalculationOpen)}
            icon={costCalculationOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowRightIcon />}
          />
        </Grid>

        {costCalculationOpen && 'percentages' in values &&
        <Grid item className={propertyClassName}>
          <CostCalculationTable
            {...props}
            values={values.percentages}
            errors={errors.percentages || []}
            touched={touched.percentages || []}
            setFieldValue={(
              type: CostCalculationPercentageType, subkey: string, value: number
            ) => {
              const index = values.percentages?.findIndex((obj: any) => obj.type === type);

              let field = values.percentages ? [...values.percentages] : [];
              field[index][subkey] = value;

              setFieldValue(
                id,
                field
              );
            }}
            setValues={setValues}
          />
        </Grid>
        }
      </Fragment>
      }
    </Grid>
  );
}